/**
 * The CPV screens' page shell: token-driven CSS variables (light and dark), the soft icon-tile header and the
 * one way a refusal is shown — the server's code as the notice title and its message verbatim.
 */
import React, { useMemo } from 'react';
import { Card, theme } from 'antd';
import PageHeader from '@/components/PageHeader';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import { StatusNotice } from '@/components/genealogy/StatusIndicator';
import './Cpv.css';

/** The `.cpv-page` CSS variables (Cpv.css) from the active theme's tokens — for a screen on the kit's CpvScreen whose body
 * still draws with the Cpv.css cards (the parameter page). */
export function useCpvPageVars() {
  const { token } = theme.useToken();
  return useMemo(() => ({
    backgroundColor: token.colorBgLayout,
    '--cpv-card': token.colorBgContainer,
    '--cpv-border': token.colorBorderSecondary,
    '--cpv-text': token.colorText,
    '--cpv-muted': token.colorTextSecondary,
    '--cpv-hover': token.colorFillQuaternary,
    '--cpv-icon': token.blue6,
    '--cpv-icon-bg': token.blue1,
  }), [token]);
}

export function CpvPage({ icon: Icon, title, subtitle, children, testId, header }) {
  const { isDark } = useTheme();
  const vars = useCpvPageVars();
  // Every CPV screen sits in one white bordered page card with the shared page header as its title (icon, title, the subtitle as the
  // description, Back on the right); the header stays put and only the body scrolls. A screen that brings its own header band (the
  // charts) shows it at the top of the body, as before.
  return (
    <div className="p-2.5 pt-5" style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}>
      <Card
        bordered={false}
        className="border border-gray-300 shadow-md"
        classNames={{ body: 'page-card-scroll' }}
        styles={{ body: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px' } }}
        style={{ backgroundColor: isDark ? defaultColors.palette.gray[800] : '#fff', height: 'calc(100dvh - 120px)', display: 'flex', flexDirection: 'column' }}
        title={<PageHeader compact icon={Icon ? <Icon size={22} /> : undefined} title={title} description={subtitle || undefined} />}
      >
        <div className="cpv-page" style={{ ...vars, backgroundColor: 'transparent', padding: 0, minHeight: 0 }} data-testid={testId}>
          {header || null}
          {children}
        </div>
      </Card>
    </div>
  );
}

export function Refusal({ refusal, children }) {
  if (!refusal) return null;
  return (
    <StatusNotice state="error" title={refusal.code}>
      <div data-testid="cpv-refusal">{refusal.message}</div>
      {children}
    </StatusNotice>
  );
}

/** A band bound the plan does not state reads "not set" — never 0, never blank. */
export const bound = (v) => (v === null || v === undefined || v === '' ? <span className="cpv-notset">not set</span> : String(v));
